<script setup lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'
import { byCountry } from 'country-code-lookup'

import { useGetProfilePicture } from '~/composables/useGetProfilePicture'

import { useTagStore } from '~/stores/tag'

import { capitalizeAll } from '~/utils'

import type { Influencer, StatsV3Influencer } from '~/types/influencer'

type Props = {
  influencer: StatsV3Influencer | Influencer
}
const props = defineProps<Props>()

const { t } = useI18n()
const { getProfilePictureUrl } = useGetProfilePicture()

const { GET_TAGS_FROM_IDS, FETCH: FETCH_TAGS } = useTagStore()
await callOnce('FETCH_TAGS', FETCH_TAGS)

const countryTags = computed(() => {
  return GET_TAGS_FROM_IDS(props.influencer.tags?.identity.country ?? [])
})
const countries = computed<string>(() => {
  return countryTags.value
    .map((tag) => t(`tags.country.${tag.name}`))
    .join(', ')
})

const kinds = computed<string>(() => {
  return GET_TAGS_FROM_IDS(
    props.influencer.tags?.identity.influencer_kind ?? [],
  )
    .map((tag) => t(`tags.influencer_kind.${tag.name}`))
    .join(', ')
})

const entity = computed<string>(() => {
  return props.influencer.entity
})

const picture = computed<string>(() => {
  return getProfilePictureUrl({
    slug: props.influencer.has_profile_picture
      ? props.influencer.slug
      : undefined,
    size: '200_200',
    target: 'profile_picture',
    kind: 'influencer',
  })
})

const flagCode = computed<string | undefined>(() => {
  for (const { name } of countryTags.value) {
    const result = byCountry(capitalizeAll(name.replace(/-/gi, ' ')))

    if (result) return result.iso2.toLowerCase()
  }
})
</script>

<template>
  <div>
    <div class="tw-flex tw-gap-2xs tw-rounded-md tw-bg-fill-alt tw-p-2xs">
      <div>
        <img
          class="tw-size-[76px] tw-rounded tw-border-2"
          :src="picture"
          alt=""
          loading="lazy"
        />
      </div>
      <div class="tw-flex tw-w-min tw-flex-col tw-gap-2xs">
        <div
          class="tw-flex tw-h-full tw-min-w-[90px] tw-items-center tw-justify-center tw-rounded-xs tw-bg-fill-alt-hover"
        >
          <p
            class="tw-text-body-xs tw-line-clamp-2 tw-p-2xs tw-text-center tw-text-white"
          >
            <b>{{ entity }}</b>
          </p>
        </div>
        <div class="tw-flex tw-gap-2xs">
          <div
            class="tw-text-body-xs tw-flex tw-items-center tw-whitespace-nowrap tw-rounded-xs tw-bg-[#737373] tw-px-xs !tw-font-medium tw-text-inverse"
          >
            {{ kinds }}
          </div>
          <div
            class="tw-flex tw-size-2xl tw-items-center tw-justify-center tw-rounded-xs tw-bg-fill-alt-hover"
          >
            <ClientOnly>
              <UIIcon
                v-if="flagCode"
                :name="`flag:${flagCode}-4x3`"
                :alt="countries"
                :title="countries"
                size="sm"
              />
            </ClientOnly>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
